import ResendUserInviteDialog from "@/components/admin/user-management/pending_users/resend_user_invite_dialog";
import DebtorChip from "@/components/shared/debtor-chip";
import DialogActionCancelSubmit from "@/components/shared/dialog/dialog-action-cancel-submit";
import DialogWrapper from "@/components/shared/dialog/dialog-wrapper";
import { create_error_code_toast } from "@/components/shared/error-code-toast";
import { clientFetch, url } from "@/components/shared/fetch-client/fetch_client";
import CopyButton from "@/components/shared/form/copy_button";
import FormField from "@/components/shared/form/form_field";
import ReadSessionWrapper from "@/components/shared/read-session-wrapper";
import TableBase, { TableIdEnum } from "@/components/shared/table/table-base";
import { FormikWrapper } from "@/components/shared/wrappers/formik-wrapper";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import ForwardToInboxIcon from "@mui/icons-material/ForwardToInbox";
import LockResetIcon from "@mui/icons-material/LockReset";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PersonPinOutlinedIcon from "@mui/icons-material/PersonPinOutlined";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import * as Yup from "yup";
import * as validation from "../../../shared/validation";
import UserDeleteDialog from "./user-delete-dialog";
import UserImpersonateDialog from "./user-impersonate-dialog";
import UserBlockDeblockDialog from "./user_block-deblock-dialog";

export default function UserTable({ debtor_id = null, url_params_enabled = true }) {
	// Hooks
	const { t } = useTranslation();
	const [count, setCount] = useState(0);
	const [rows, setRows] = useState([]);

	const onChangeHandler = async (params, setLoading) => {
		// Make API call
		let uri = url`/api/back-office/user`;

		if (debtor_id) {
			uri = url`/api/back-office/debtor/${debtor_id}/users`;
		}

		// Make fetch call
		try {
			const { ok, body } = await clientFetch(uri, {
				params: { ...params },
			}).get();

			if (!ok) {
				return null;
			}

			setCount(body.count);
			setRows(body.data);
			setLoading(false);
		} catch (error) {
			setLoading(false);
			create_error_code_toast(t, error);
		}
	};

	const additionalFilters = [
		{
			key: "show_disabled",
			label: t("back_office.customers.user_management.show_disabled"),
			type: "switch",
		},
	];

	if (!debtor_id) {
		additionalFilters.unshift({
			key: "membership_type",
			label: t("back_office.customers.user_management.membership_type.label"),
			option_list: [
				{
					label: t("back_office.customers.user_management.membership_type.private"),
					value: "private",
				},
				{
					label: t("back_office.customers.user_management.membership_type.business"),
					value: "business",
				},
			],
			type: "select",
		});
	}

	return (
		<Box sx={{ my: 3 }}>
			<TableBase
				enable_search
				enable_reset_button
				sort_default="asc"
				order_by_default="user_id"
				on_change={onChangeHandler}
				total_count={count}
				table_id={TableIdEnum.USER_TABLE_BACK_OFFICE}
				additional_filters={additionalFilters}
				url_params_enabled={url_params_enabled}
				column_list={[
					{
						key: "otp_enabled",
						label: t("back_office.customers.user_management.table.headers.otp"),
						width: "75px",
					},
					{
						key: "username",
						label: t("back_office.customers.user_management.table.headers.username"),
					},
					{
						key: "email_address",
						label: t("back_office.customers.user_management.table.headers.email_address"),
						width: "300px",
					},
					{
						key: "categories",
						label: t("back_office.customers.user_management.table.headers.categories"),
					},
					{
						key: "phone_number",
						label: t("back_office.customers.user_management.table.headers.phone_number"),
						sortable: false,
					},
					{
						key: "language",
						label: t("back_office.customers.user_management.table.headers.language"),
						width: "100px",
					},
					{
						key: "debtor_code",
						label: t("back_office.customers.user_management.table.headers.debtor_code"),
					},
					{
						key: "membership_type",
						label: t("back_office.customers.user_management.table.headers.membership_type"),
					},
					{
						key: "company",
						label: t("back_office.customers.user_management.table.headers.company"),
					},
					{
						key: "row_actions",
						sortable: false,
					},
				]}
				row_list={rows.map((row) => ({
					cell_list: [
						{
							key: "otp_enabled",
							value: (
								<Tooltip
									title={
										row.otp_enabled
											? t("back_office.customers.user_management.table.body.otp.enabled")
											: t("back_office.customers.user_management.table.body.otp.disabled")
									}
								>
									{row.otp_enabled ? (
										<CheckCircleIcon sx={{ color: "success.main" }} />
									) : (
										<ErrorIcon sx={{ color: "warning.main" }} />
									)}
								</Tooltip>
							),
						},
						{
							key: "username",
							value: (
								<>
									<Typography variant="body3" sx={row.is_admin && { mr: 1 }}>
										{row.username === row.debtor_code
											? row.username
											: `${row.username}@${row.debtor_code}`}
									</Typography>
									{row.is_admin && <Chip label="Admin" size="small" variant="outlined" />}
									{row.blocked && (
										<Tooltip
											title={t("back_office.customers.debtor.settings.block_debtor.blocked")}
											arrow
										>
											<BlockIcon sx={{ height: 22, ml: 1, verticalAlign: "middle" }} />
										</Tooltip>
									)}
								</>
							),
						},
						{
							key: "email_address",
							value: row.email_address,
						},
						{
							key: "categories",
							value:
								row?.categories &&
								row.categories.map((category, index) => {
									return (
										<Chip key={`${index}-${category}`} label={category.name} sx={{ m: 0.25 }} />
									);
								}),
						},
						{
							key: "phone_number",
							value: (
								<>
									{`${row.dial_code} ${row.phone_number_digits}`}
									<CopyButton
										value={`${row.dial_code} ${row.phone_number_digits}`}
										icon_sx={{ height: 20, width: 20 }}
									/>
								</>
							),
						},
						{
							key: "language",
							value: row.language,
						},
						{
							key: "debtor_code",
							value: (
								<DebtorChip
									debtor_code={
										row?.debtor_code ? row.debtor_code : t("validation.debtor_code_unverified")
									}
									debtor_id={row.debtor_id}
									is_back_office={true}
								/>
							),
						},
						{
							key: "membership_type",
							value: row.membership_type,
						},
						{
							key: "company",
							value: row?.company || "-",
						},
						{
							key: "row_actions",
							value: <RowActions row={row} rows={rows} set_rows={setRows} />,
						},
					],
					sx: { backgroundColor: row.disabled && "card_shine_through_error.main" },
				}))}
			/>
		</Box>
	);
}

/* -------------------------------------------------------------------------- */
/*                           CUSTOM ACTIONS SECTION                           */
/* -------------------------------------------------------------------------- */
const RowActions = ({ row, rows, set_rows }) => {
	const { t } = useTranslation();

	/* ---------------------------------- HOOKS --------------------------------- */
	const [deleteDialog, setDeleteDialog] = useState(false);
	const [blockDeblockDialog, setBlockDeblockDialog] = useState(false);
	const [impersonateDialog, setImpersonateDialog] = useState(false);
	const [openResendDialog, setOpenResendDialog] = useState(false);

	/* -------------------------------- HANDLERS -------------------------------- */
	const handleResendUserInvite = async () => {
		const { status } = await clientFetch(url`/api/back-office/user/${row.user_id}/email`).post();

		switch (status) {
			case 202:
				// Close dialog
				setOpenResendDialog(false);

				// Notify update
				toast.success(
					<Trans
						i18nKey="administrator.user_management.pending.resend_modal.status.success"
						values={{ username: row?.username }}
					/>,
				);

				break;
			default:
				toast.error(t("validation.errors.unknown"));
				break;
		}
	};

	return (
		<Box display="flex" alignItems="center">
			<Button
				disabled={row.disabled || !row.verified}
				fullWidth
				size="small"
				color="company_light"
				variant="outlined"
				startIcon={<PersonPinOutlinedIcon />}
				onClick={() => setImpersonateDialog(true)}
			>
				{t("back_office.customers.user_management.table.body.buttons.impersonate")}
			</Button>
			<UserActionsMenu
				set_rows={set_rows}
				row={row}
				t={t}
				set_open_resend_dialog={setOpenResendDialog}
				set_block_deblock_dialog={setBlockDeblockDialog}
				set_delete_dialog={setDeleteDialog}
			/>

			{/* ------------------------------ DELETE DIALOG ----------------------------- */}
			<UserDeleteDialog
				set_rows={set_rows}
				rows={rows}
				open={deleteDialog}
				on_close={() => setDeleteDialog(false)}
				target_user={row}
			/>

			{/* -------------------------- BLOCK DEBLOCK DIALOG -------------------------- */}
			<UserBlockDeblockDialog
				set_rows={set_rows}
				rows={rows}
				open={blockDeblockDialog}
				on_close={() => setBlockDeblockDialog(false)}
				target_user={row}
			/>

			{/* --------------------------- IMPERSONATE DIALOG --------------------------- */}
			<UserImpersonateDialog
				open={impersonateDialog}
				on_close={() => setImpersonateDialog(false)}
				target_user={row}
			/>

			{/* -------------------------- RESEND INVITE DIALOG -------------------------- */}
			<ResendUserInviteDialog
				username={row.username}
				open_resend_dialog={openResendDialog}
				set_open_resend_dialog={setOpenResendDialog}
				on_submit_handler={handleResendUserInvite}
			/>
		</Box>
	);
};

/* -------------------------------------------------------------------------- */
/*                              USER ACTIONS MENU                             */
/* -------------------------------------------------------------------------- */
const UserActionsMenu = ({
	set_rows,
	row,
	t,
	set_open_resend_dialog,
	set_block_deblock_dialog,
	set_delete_dialog,
}) => {
	// Hooks
	const [anchorEl, setAnchorEl] = useState(null);
	const [dialogOtpDisable, setDialogOtpDisable] = useState(false);
	const open = Boolean(anchorEl);
	// Handlers
	const handleClick = (event) => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	return (
		<Box>
			<IconButton aria-haspopup="true" aria-expanded={open} onClick={handleClick} sx={{ ml: 1 }}>
				<MoreVertIcon />
			</IconButton>
			<Menu anchorEl={anchorEl} keepMounted open={open} onClose={handleClose}>
				{/* RESEND VERIFICATION EMAIL  */}
				{(row.disabled || !row.verified) && (
					<MenuItem
						onClick={() => {
							handleClose();
							set_open_resend_dialog(true);
						}}
					>
						<ForwardToInboxIcon sx={{ mr: 1 }} />
						{t("buttons.resend")}
					</MenuItem>
				)}

				{/* BLOCK/DEBLOCK USER  */}
				<MenuItem
					disabled={row.disabled}
					onClick={() => {
						handleClose();
						set_block_deblock_dialog(true);
					}}
				>
					<BlockIcon sx={{ mr: 1 }} />
					{row.blocked === true
						? t("back_office.customers.user_management.table.body.buttons.deblock")
						: t("back_office.customers.user_management.table.body.buttons.block")}
				</MenuItem>

				{/* DELETE USER  */}
				<MenuItem
					disabled={row.disabled}
					onClick={() => {
						handleClose();
						set_delete_dialog(true);
					}}
				>
					<PersonRemoveIcon sx={{ mr: 1 }} />
					{t("buttons.delete")}
				</MenuItem>

				{/* DISABLE OTP  */}
				{row.otp_enabled ? (
					<MenuItem
						onClick={() => {
							handleClose();
							setDialogOtpDisable(true);
						}}
					>
						<LockResetIcon sx={{ mr: 1 }} />

						{t("back_office.customers.user_management.table.body.buttons.disable_otp")}
					</MenuItem>
				) : null}
			</Menu>

			{/* DISABLE OTP DIALOG  */}
			<DisableOtpDialog
				set_rows={set_rows}
				open={dialogOtpDisable}
				on_close={() => setDialogOtpDisable(false)}
				row={row}
			/>
		</Box>
	);
};

function DisableOtpDialog({ open, on_close, row, set_rows }) {
	// HOOKS
	const { t } = useTranslation();
	const [loading, setLoading] = useState(false);

	// FORMIK
	const initValues = {
		otp: "",
	};

	const validationSchema = Yup.object().shape({
		otp: validation.inputOtp(t),
	});

	// HANDLERS
	const submitHandler = async (values) => {
		setLoading(true);

		const { status, body } = await clientFetch(url`/api/back-office/otp/${row.user_id}`, {
			json: values,
		}).delete();

		setLoading(false);

		switch (status) {
			case 202:
				set_rows((rows) =>
					rows.map((rowCurrent) =>
						rowCurrent.user_id === row.user_id ? { ...row, otp_enabled: false } : rowCurrent,
					),
				);
				toast.success(
					<Trans
						i18nKey="back_office.customers.user_management.dialog.disable_otp.messages.success"
						values={{ user: row.username }}
					/>,
				);
				on_close();
				return;
			case 406: {
				switch (body.detail) {
					case "OTP not valid":
						return toast.error(
							t("back_office.customers.user_management.dialog.disable_otp.otp_invalid"),
						);

					case "OTP Not enabled for this user":
						return toast.error(
							t("back_office.customers.user_management.dialog.disable_otp.otp_not_enabled"),
						);
					default:
						return toast.error(t("validation.errors.unknown"));
				}
			}

			default:
				return toast.error(t("validation.errors.unknown"));
		}
	};
	return (
		<ReadSessionWrapper>
			{(session) => (
				<DialogWrapper
					close_button
					disable_escape
					title={t("back_office.customers.user_management.dialog.disable_otp.title")}
					open={open}
					on_close={on_close}
				>
					<FormikWrapper
						onSubmit={submitHandler}
						initialValues={initValues}
						validationSchema={validationSchema}
					>
						{session.user.otp_enabled ? (
							<>
								<Typography>
									<Trans
										i18nKey="back_office.customers.user_management.dialog.disable_otp.description"
										values={{ user: row.username }}
									/>
								</Typography>
								<Paper variant="outlined" sx={{ my: 1, pb: 1.5, pt: 0.5, px: 2 }}>
									<Typography sx={{ mb: 0.5, mt: 1 }} fontWeight={500}>
										{t("inputs.otp.label")}
									</Typography>

									<FormField
										name="otp"
										type="text"
										size="small"
										label=""
										placeholder={t("inputs.otp.placeholder")}
										auto_focus
										show_error_on_exit
									/>
								</Paper>
								<DialogActionCancelSubmit
									loading={loading}
									on_close={on_close}
									on_submit_text={t(
										"back_office.customers.user_management.dialog.disable_otp.submit_button",
									)}
								/>
							</>
						) : (
							<Alert severity="info" sx={{ mb: 2, mt: 2 }}>
								{t("back_office.customers.user_management.dialog.disable_otp.otp_first")}
							</Alert>
						)}
					</FormikWrapper>
				</DialogWrapper>
			)}
		</ReadSessionWrapper>
	);
}
